import { Suspense, SyntheticEvent, lazy, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';

import { CoreAlert, CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTooltip, CoreTypography } from '@youscience/core';
import {
  StateSpotlightLocationKey,
  StateSpotlightLocationOptions,
  getStateSpotlightLocationValue,
} from '@components/StateSpotlight';
import { UsheIcon } from '@components/UsheIcon';
import { Major, MajorsResult } from '@common-types';
import { Chip, Autocomplete as MuiAutocomplete, TextField } from '@mui/material';

import { AutocompleteOption, Option, SelectedFiltersProps } from '@interfaces';

import { LocationOption, SCHOOL_FILTERS_DATA } from '@constants/fullSchoolsList';
import { CLARIFY_COST_TOOLTIP } from '@constants';
import { STATES_OF_USA } from '@utils/states';

import { Autocomplete } from '@components/Form/Autocomplete';
import { renderNoOptionsText } from '@components/Form/Autocomplete/Autocomplete';
import { FilterAccordion } from '@components/FilterAccordion';
import { CheckboxGroup } from '@components/Form/Checkbox';
import { Search } from '@components/Search';
import ActionButton from '@components/ActionButton';

import { getOptions } from '@utils/form';
import { moneyFormatter } from '@utils/formatter';

import useSchoolSearchFiltersStore, { SchoolSearchState } from '@store/SchoolSearchFiltersStore';

import { CollegeSearchFormValues } from '@pages/SchoolList/SchoolList';

import { baseService } from '@services/base.service';

import RefreshIcon from '@images/refresh.svg?react';
import InfoIcon from '@images/info.svg?react';

import { Range } from '../Range';

import { sxStyles, StyledFilterSectionWrap, StyledAverageCostTitleWrap } from './Filters.styles';

import { useSchools } from '@hooks/useSchools';
import { useStateSpotlight } from '@hooks/useStateSpotlight';
import {
  STATE_PUBLIC_COLLEGE_FILTER,
  getStatePublicCollegesCountLabel,
  isStatePublicCollegeOnlyFilter,
} from '@constants/stateSpotlight';

interface FilterProps {
  isExpanded?: boolean;
  areaOfStudyTitle: string;
  paginationLimit: number;
  selectedValue: SelectedFiltersProps[];
  defaultValues: Record<string, unknown>;
  searchFilterValue: string;
  formValues: UseFormReturn<CollegeSearchFormValues, unknown>;
  handleChangeFilters: (filter: keyof SchoolSearchState) => (values: string[]) => void;
  resetPaginationPage: () => void;
  setSearchFilterValue: React.Dispatch<React.SetStateAction<string>>;
}

const SaveFilter = lazy(() => import('../SaveFilters'));

export const Filters = (props: FilterProps) => {
  const { cipCode } = useParams();
  const navigate = useNavigate();
  const filterByMajor = !!cipCode;

  const {
    isExpanded,
    areaOfStudyTitle,
    paginationLimit,
    selectedValue,
    defaultValues,
    searchFilterValue,
    formValues,
    handleChangeFilters,
    resetPaginationPage,
    setSearchFilterValue,
  } = props;

  const { control, getValues, setValue, reset } = formValues;

  const {
    removeSearchFilterValue,
    filters,
    isLoading,
    resetSearchFilters,
    removeSearchFilter,
    setSearchFilters,
    costFilter,
  } = useSchoolSearchFiltersStore((state) => state);

  const [majors, setMajors] = useState<Major[]>([]);
  const [regions, setRegions] = useState<AutocompleteOption[]>([]);
  const [isShowSearchAlert, setIsShowSearchAlert] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { data: schoolsData, refetch: getSchools, isLoading: isSchoolsLoading } = useSchools({ filterByMajor });
  const { spotlight } = useStateSpotlight();

  const isStatePublicCollegeCount = !!spotlight && isStatePublicCollegeOnlyFilter(filters);
  const totalCount = schoolsData?.totalCount ?? 0;
  const resultCountLabel =
    isStatePublicCollegeCount && spotlight
      ? getStatePublicCollegesCountLabel(totalCount, spotlight.publicCollegeIds.length)
      : `${totalCount} results`;

  useEffect(() => {
    const getMajors = async (cipCode: string) => {
      const majorResult = await baseService.getAsync<MajorsResult>(`/majors`, {
        params: { cipCodes: cipCode, limit: 1000 },
      });

      setMajors(majorResult.majors);
    };

    if (filterByMajor) {
      const areaOfStudyCode = cipCode.split('.')[0];

      void getMajors(areaOfStudyCode);
    }
  }, [filterByMajor]);

  const scrollToElement = (cipCode: string) => {
    const selectedElement = document.querySelectorAll(`input[value="${cipCode}"]`);

    const elementRow = selectedElement.length
      ? (selectedElement[0] as HTMLElement)?.parentElement?.parentElement
      : null;

    const scrollContainer = document.getElementById('cipCodesWrap');

    if (elementRow && scrollContainer) {
      scrollContainer.scrollTop = elementRow.offsetTop || 0;
      setScrolled(true);
    }
  };

  if (filterByMajor && !scrolled) {
    scrollToElement(cipCode);
  }

  const updateRegions = () => {
    const initialRegion: LocationOption[] = [];

    SCHOOL_FILTERS_DATA.location.filters.forEach((regionOption) => {
      regionOption.additionData?.forEach((state) => {
        if ((filters.states as string[])?.includes(state.value)) {
          initialRegion.push(regionOption as unknown as LocationOption);
        }
      });
    });

    const uniqueRegions = initialRegion.reduce(
      (acc, state) => {
        if (acc.map[state.value]) return acc;

        acc.map[state.value] = true;
        acc.states.push(state);
        return acc;
      },
      {
        map: {} as { [key: string]: boolean },
        states: [] as LocationOption[],
      },
    ).states;

    setRegions(uniqueRegions);
  };

  useEffect(() => {
    updateRegions();
  }, [filters.states]);

  const handleSelectRegion = (event: SyntheticEvent, values: readonly AutocompleteOption[]) => {
    const locationField = SCHOOL_FILTERS_DATA.location.fieldName as keyof CollegeSearchFormValues;

    setRegions(values as AutocompleteOption[]);

    if (values?.length) {
      const states = values.reduce((acc, { additionData }) => {
        const statesByRegion: string[] = [];

        additionData?.forEach(({ value }) => statesByRegion.push(value));

        return [...acc, ...statesByRegion];
      }, [] as string[]);

      setValue(locationField, states);
      setSearchFilters({ states });
    } else {
      setValue(locationField, []);
      removeSearchFilter(locationField);
    }
  };

  const handleChangeStates = (name: string, value: AutocompleteOption | readonly AutocompleteOption[] | null) => {
    const locationField = SCHOOL_FILTERS_DATA.location.fieldName as keyof CollegeSearchFormValues;

    if ((value as AutocompleteOption[])?.length) {
      const newValues = new Set([
        ...(value as AutocompleteOption[] | string[]).map((option) => {
          if (typeof option === 'string') {
            return option;
          }

          return option.value;
        }),
      ]);

      const states = Array.from(newValues);

      setValue(locationField, states);
      setSearchFilters({ states });
    } else {
      setValue(locationField, []);
      removeSearchFilter(locationField);
    }

    resetPaginationPage();
  };

  const getMajors = () => {
    if (majors.length) {
      return getOptions(majors, 'cipTitle', 'cipCode');
    }

    return [];
  };

  const handleCostFilter = (name: string, cost: number | number[]) => {
    const costFieldName = SCHOOL_FILTERS_DATA.averageCost.fieldName;

    if (cost === costFilter.max) {
      removeSearchFilter(costFieldName);
    } else {
      setSearchFilters({ [costFieldName]: cost as number });
    }

    setValue(costFieldName as keyof CollegeSearchFormValues, cost as number);

    resetPaginationPage();
  };

  const handleChangeHasScholarship = (event: SyntheticEvent, checked: boolean) => {
    setValue('hasScholarship', checked);
    setSearchFilters({ hasScholarship: checked });

    resetPaginationPage();
  };

  const handleChangeStateSpotlightLocation = (key: StateSpotlightLocationKey, checked: boolean) => {
    if (checked && spotlight) {
      setSearchFilters({ [key]: getStateSpotlightLocationValue(key, spotlight) });
    } else {
      removeSearchFilter(key);
    }

    resetPaginationPage();
  };

  const handleDeleteFilterChip = (val: SelectedFiltersProps) => {
    const deletedValue = getValues(val.key as keyof CollegeSearchFormValues);

    removeSearchFilterValue(val);

    if (Array.isArray(deletedValue)) {
      const currentValue = deletedValue.filter((value) => value !== val.value);

      setValue(val.key as keyof CollegeSearchFormValues, currentValue);
    } else if (val.key === SCHOOL_FILTERS_DATA.averageCost.fieldName) {
      setValue(val.key as keyof CollegeSearchFormValues, costFilter?.max);
    }

    if (val.key === SCHOOL_FILTERS_DATA.search.fieldName) {
      setSearchFilterValue('');
    }

    resetPaginationPage();
  };

  const resetFilters = async () => {
    resetSearchFilters({ limit: paginationLimit });
    await getSchools();
  };

  const handleResetFilters = () => {
    resetPaginationPage();
    setSearchFilterValue('');
    reset(defaultValues);
    resetFilters();

    if (filterByMajor) {
      navigate('/schools');
    }
  };

  const handleOffSearchAlert = () => setIsShowSearchAlert(false);

  const handleResetSearch = () => {
    removeSearchFilter(SCHOOL_FILTERS_DATA.search.fieldName);

    handleOffSearchAlert();
    setSearchFilterValue('');

    resetPaginationPage();
  };

  const handleRadioChange = (filters: SchoolSearchState) => {
    resetSearchFilters({ limit: paginationLimit });
    setSearchFilters(filters);
  };

  const handleSchoolSearch = (value: string) => {
    let keywords: string | undefined = value?.trim();

    if (keywords.length < 3) {
      setIsShowSearchAlert(true);
      keywords = undefined;
    } else {
      handleOffSearchAlert();
    }

    if (keywords) {
      setSearchFilters({ keywords });
    } else {
      removeSearchFilter(SCHOOL_FILTERS_DATA.search.fieldName);
    }

    setSearchFilterValue(value);
    resetPaginationPage();
  };

  const saveFilterSection = (isExpanded?: boolean) => {
    return (
      <CoreBox>
        {selectedValue.length > 0 && (
          <ActionButton
            data-testid="clearAllFilters"
            sx={sxStyles.clearBtn}
            type="button"
            onClick={handleResetFilters}
            busyText="Clearing..."
            variant="text"
            endIcon={<RefreshIcon />}
          >
            Clear all Filters
          </ActionButton>
        )}

        {!filterByMajor && (
          <Suspense fallback={<div />}>
            <SaveFilter
              control={control}
              filters={filters}
              isExpanded={isExpanded}
              handleRadioChange={handleRadioChange}
            />
          </Suspense>
        )}
      </CoreBox>
    );
  };

  const areasOfStudy = useMemo(() => {
    let areasOfStudy;

    if (filterByMajor) {
      areasOfStudy = getMajors();
    } else {
      areasOfStudy = SCHOOL_FILTERS_DATA.areasOfStudy.filters as Option[];
    }

    return areasOfStudy.sort((a, b) => a.label?.localeCompare(b.label ?? '') ?? 0) as Option[];
  }, [filterByMajor, majors]);

  return (
    <StyledFilterSectionWrap data-testid="filterSectionWrap">
      <CoreBox>
        <CoreTypography sx={sxStyles.filterSectionTitle} component="div" variant="h5">
          Filter by:
        </CoreTypography>

        <CoreBox sx={sxStyles.searchWrap as object}>
          <Search
            data-testid="schoolSearch"
            onEnterSearch={handleSchoolSearch}
            onClear={handleResetSearch}
            filterValue={searchFilterValue}
            onBlur={handleOffSearchAlert}
          />

          {isShowSearchAlert && (
            <CoreBox sx={sxStyles.alertWrap as object}>
              <CoreAlert severity="warning" description="Please enter at least three characters" />
            </CoreBox>
          )}
        </CoreBox>

        {(isStatePublicCollegeCount || (schoolsData?.schools?.length ?? 0) > 0) && (
          <CoreTypography data-testid="resultCount" sx={sxStyles.filterSectionTitle} component="div">
            {isLoading || isSchoolsLoading ? 'Searching...' : resultCountLabel}
          </CoreTypography>
        )}

        <div data-testid="filterChips">
          {selectedValue.map((val) => {
            const deleteFilterItem = () => handleDeleteFilterChip(val);

            return (
              <CoreTooltip key={`${val.value}${val.key as string}`} title={val.label}>
                <Chip
                  sx={sxStyles.selectedValue}
                  icon={(val.key as string) === STATE_PUBLIC_COLLEGE_FILTER ? <UsheIcon /> : undefined}
                  variant={(val.key as string) === STATE_PUBLIC_COLLEGE_FILTER ? 'outlined' : 'filled'}
                  label={val.label}
                  color="primary"
                  onDelete={deleteFilterItem}
                />
              </CoreTooltip>
            );
          })}
        </div>

        {saveFilterSection(isExpanded)}
      </CoreBox>

      <CoreBox data-testid="filterWrap">
        {spotlight && (
          <FilterAccordion
            data-testid="stateSpotlightLocation"
            isExpanded={isExpanded}
            accordionTitle={SCHOOL_FILTERS_DATA.location.title}
          >
            <StateSpotlightLocationOptions
              spotlight={spotlight}
              filters={filters}
              onChange={handleChangeStateSpotlightLocation}
            />
          </FilterAccordion>
        )}

        <FilterAccordion
          data-testid="schoolType"
          isExpanded={isExpanded}
          accordionTitle={SCHOOL_FILTERS_DATA.schoolsType.title}
        >
          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.schoolsType.fieldNames.carnegieSizeSetting}
            options={SCHOOL_FILTERS_DATA.schoolsType.filters.carnegieSizeSetting}
            onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.schoolsType.fieldNames.carnegieSizeSetting)}
          />

          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.schoolsType.fieldNames.institutionType}
            options={SCHOOL_FILTERS_DATA.schoolsType.filters.institutionType}
            onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.schoolsType.fieldNames.institutionType)}
          />
        </FilterAccordion>

        <FilterAccordion isExpanded={isExpanded} accordionTitle={SCHOOL_FILTERS_DATA.location.title}>
          <MuiAutocomplete
            data-testid="regionsAutocomplete"
            sx={sxStyles.locationField}
            multiple
            size="small"
            options={SCHOOL_FILTERS_DATA.location.filters}
            value={regions}
            onChange={handleSelectRegion}
            renderInput={(params) => <TextField {...params} label="Region" placeholder="" />}
            clearIcon={null}
            noOptionsText={renderNoOptionsText()}
          />

          <Autocomplete
            data-testid="statesAutocomplete"
            isMultiple
            control={control}
            name={SCHOOL_FILTERS_DATA.location.fieldName}
            options={STATES_OF_USA as Option[]}
            label="States"
            limitTags={9}
            onChange={handleChangeStates}
          />
        </FilterAccordion>

        <FilterAccordion
          data-testid="cipCodes"
          isExpanded={isExpanded}
          accordionTitle={filterByMajor ? areaOfStudyTitle : SCHOOL_FILTERS_DATA.areasOfStudy.title}
        >
          <CoreBox id="cipCodesWrap" sx={sxStyles.cipCodesWrap}>
            <CheckboxGroup
              control={control}
              name={SCHOOL_FILTERS_DATA.areasOfStudy.fieldName}
              options={areasOfStudy}
              onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.areasOfStudy.fieldName)}
              isShowTooltip
            />
          </CoreBox>
        </FilterAccordion>

        <FilterAccordion
          data-testid="specialty"
          isExpanded={isExpanded}
          accordionTitle={SCHOOL_FILTERS_DATA.specialty.title}
        >
          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.specialty.fieldName}
            options={SCHOOL_FILTERS_DATA.specialty.filters}
            onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.specialty.fieldName)}
          />
        </FilterAccordion>

        <FilterAccordion data-testid="degree" isExpanded={isExpanded} accordionTitle={SCHOOL_FILTERS_DATA.degree.title}>
          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.degree.fieldName}
            options={SCHOOL_FILTERS_DATA.degree.filters}
            onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.degree.fieldName)}
          />
        </FilterAccordion>

        <FilterAccordion
          data-testid="enrollmentSize"
          isExpanded={isExpanded}
          accordionTitle={SCHOOL_FILTERS_DATA.enrollmentSize.title}
        >
          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.enrollmentSize.fieldName}
            options={SCHOOL_FILTERS_DATA.enrollmentSize.filters}
            onChange={handleChangeFilters(SCHOOL_FILTERS_DATA.enrollmentSize.fieldName)}
          />
        </FilterAccordion>

        <CoreBox sx={sxStyles.averageCostSliderWrap}>
          <StyledAverageCostTitleWrap>
            <CoreTypography sx={sxStyles.averageCostTitle} component="div" variant="subtitle2">
              Average Cost, per Year
            </CoreTypography>

            <CoreTooltip title={CLARIFY_COST_TOOLTIP}>
              <CoreBox>
                <InfoIcon />
              </CoreBox>
            </CoreTooltip>
          </StyledAverageCostTitleWrap>

          <Range
            testid="averageCostSlider"
            name={SCHOOL_FILTERS_DATA.averageCost.fieldName}
            control={control}
            step={costFilter?.step}
            valueLabelDisplay="auto"
            min={costFilter?.min}
            max={costFilter?.max}
            onChange={handleCostFilter}
            valueLabelFormat={(value) => moneyFormatter.format(value)}
          />

          <CoreFormControlLabel
            sx={{ margin: '1rem 0' }}
            label={<CoreTypography>{SCHOOL_FILTERS_DATA.hasScholarship.title}</CoreTypography>}
            control={<CoreCheckbox checked={filters.hasScholarship || false} onChange={handleChangeHasScholarship} />}
          />
        </CoreBox>

        <FilterAccordion
          data-testid="settings"
          isExpanded={isExpanded}
          accordionTitle={SCHOOL_FILTERS_DATA.settings.title}
        >
          <CheckboxGroup
            control={control}
            name={SCHOOL_FILTERS_DATA.settings.fieldName}
            options={SCHOOL_FILTERS_DATA.settings.filters}
            onChange={handleChangeFilters('locales')}
          />
        </FilterAccordion>
      </CoreBox>
    </StyledFilterSectionWrap>
  );
};
